import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Clock3, ChevronRight, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAIAgents } from "@/hooks/useAIAgents";
import { useSecurityIssues, useSecurityIssueStats } from "@/hooks/useSecurityIssues";
import { useOrganizationContext } from "@/providers/OrganizationProvider";
import { useTenant } from "@/providers/TenantProvider";
import { AgentProviderLogo } from "@/components/security/AgentProviderLogo";
import type { SecurityIssueStats } from "@/services/securityIssuesService";
import type { Agent, SecurityIssue, SeverityLevel } from "@/types/security";

const rangeDays = { "24h": 1, "7d": 7, "30d": 30, "90d": 90 } as const;
const severityOrder: SeverityLevel[] = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];
const severityStyle: Record<string, { text: string; badge: string; background: string }> = {
  CRITICAL: { text: "text-[hsl(var(--chart-critical))]", badge: "border-[hsl(var(--chart-critical))] bg-[hsl(var(--chart-critical))]/10 text-[hsl(var(--chart-critical))]", background: "hsl(var(--chart-critical) / 0.1)" },
  HIGH: { text: "text-[hsl(var(--chart-high))]", badge: "border-[hsl(var(--chart-high))] bg-[hsl(var(--chart-high))]/10 text-[hsl(var(--chart-high))]", background: "hsl(var(--chart-high) / 0.1)" },
  MEDIUM: { text: "text-[hsl(var(--chart-medium))]", badge: "border-[hsl(var(--chart-medium))] bg-[hsl(var(--chart-medium))]/10 text-foreground", background: "hsl(var(--chart-medium) / 0.1)" },
  LOW: { text: "text-[hsl(var(--chart-low))]", badge: "border-[hsl(var(--chart-low))] bg-[hsl(var(--chart-low))]/10 text-[hsl(var(--chart-low))]", background: "hsl(var(--chart-low) / 0.1)" },
};

const sentenceCase = (value: string) => value.toLowerCase().replace(/(^|_)([a-z])/g, (_, space, letter) => `${space ? " " : ""}${letter.toUpperCase()}`);
const plural = (count: number, singular: string, pluralForm = `${singular}s`) => `${count} ${count === 1 ? singular : pluralForm}`;

function PanelSkeleton({ className = "h-44" }: { className?: string }) {
  return <Skeleton className={`w-full rounded-[15px] ${className}`} />;
}

function buildFunnelPath(values: Array<number | null>) {
  const knownMax = Math.max(...values.filter((value): value is number => value !== null), 1);
  const heights = values.map((value) => value === null ? 52 : 52 + Math.sqrt(value / knownMax) * 124);
  const points = heights.map((height, index) => ({ x: index * 200, y: 188 - height }));
  let path = `M 0 188 L 0 ${points[0].y}`;
  for (let index = 0; index < points.length - 1; index += 1) {
    const current = points[index];
    const next = points[index + 1];
    const midpoint = (current.x + next.x) / 2;
    path += ` C ${midpoint} ${current.y}, ${midpoint} ${next.y}, ${next.x} ${next.y}`;
  }
  path += ` L 1000 ${points[points.length - 1].y}`;
  return `${path} L 1000 188 Z`;
}

function ExposureFunnel({ agents, issues, organizationName, range }: { agents: Agent[]; issues: SecurityIssue[]; organizationName: string; range: keyof typeof rangeDays }) {
  const openIssues = issues.filter((issue) => issue.status === "OPEN" || issue.status === "IN_REVIEW");
  const agentsWithOpenIssues = new Set(openIssues.flatMap((issue) => issue.agentId == null ? [] : [issue.agentId])).size;
  const criticalHighIssues = openIssues.filter((issue) => issue.severity === "CRITICAL" || issue.severity === "HIGH");
  const agentsWithCriticalHigh = new Set(criticalHighIssues.flatMap((issue) => issue.agentId == null ? [] : [issue.agentId])).size;
  const monitoredAgents = agents.filter((a) => a.traceCount > 0);
  // TODO(data): No issue SLA deadline exists, so the breaching-SLA stage cannot be computed.
  const stages = [
    { label: "Agents discovered", value: agents.length, secondary: `Auto-discovered in ${organizationName}` },
    { label: "Monitored", value: monitoredAgents.length, secondary: `${agents.length - monitoredAgents.length} unmanaged` },
    { label: "With open issues", value: agentsWithOpenIssues, secondary: plural(openIssues.length, "open issue") },
    { label: "With critical or high", value: agentsWithCriticalHigh, secondary: plural(criticalHighIssues.length, "issue") },
    { label: "Breaching SLA", value: null, secondary: "SLA data unavailable" },
  ];
  const path = buildFunnelPath(stages.map((stage) => stage.value));
  const stageColors = ["--funnel-1", "--funnel-2", "--funnel-3", "--funnel-4", "--funnel-5"];
  const rangeLabel = range === "24h" ? "Last 24 hours" : `Last ${rangeDays[range]} days`;

  return (
    <Card className="rounded-[15px] shadow-none">
      <CardHeader className="flex-row items-start justify-between gap-4 p-4 pb-2 sm:p-5 sm:pb-2">
        <div>
          <CardTitle className="text-base font-medium">Exposure</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">Every agent found, narrowed to what needs a human today</p>
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">{rangeLabel} · silhouette indicative, counts exact</span>
      </CardHeader>
      <CardContent className="px-4 pb-5 pt-4 sm:px-4">
        <div className="grid grid-cols-5 gap-2" aria-label="Agent exposure funnel">
          {stages.map((stage, index) => (
            <div
              key={stage.label}
              className="flex min-w-0 flex-col overflow-hidden rounded-[10px]"
              style={{ backgroundColor: `hsl(var(${stageColors[index]}) / 0.07)` }}
            >
              <div className="px-3 pb-2 pt-3">
                <p className="text-[28px] font-semibold leading-none tabular-nums">{stage.value ?? "—"}</p>
                <p className="mt-1 truncate border-b border-foreground/10 pb-2 text-xs font-medium text-muted-foreground">{stage.label}</p>
                <p className="mt-2 truncate text-[11px] text-muted-foreground">{stage.secondary}</p>
              </div>
              <div className="mt-auto h-[268px] overflow-hidden rounded-b-[10px]">
                <svg className="h-full w-full" viewBox={`${index * 200} 0 200 188`} preserveAspectRatio="none" aria-hidden="true">
                  <path d={path} fill={`hsl(var(${stageColors[index]}))`} />
                </svg>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function OpenIssuesBySeverity({ stats, range }: { stats: SecurityIssueStats | undefined; range: keyof typeof rangeDays }) {
  const counts = stats?.severity_counts ?? {};
  const trends = stats?.severity_trends ?? {};
  const total = severityOrder.reduce((sum, severity) => sum + Number(counts[severity.toLowerCase()] ?? 0), 0);
  // TODO(data): The stats contract does not confirm that severity_counts excludes closed issues.
  return (
    <Card className="rounded-[15px] shadow-none">
      <CardHeader className="flex-row items-center justify-between p-4 sm:p-6">
        <CardTitle className="text-base font-medium">Open issues by severity</CardTitle>
        <span className="text-sm text-muted-foreground"><span className="font-medium tabular-nums text-foreground">{total}</span> issues · last {range === "24h" ? "24 hours" : `${rangeDays[range]} days`}</span>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-[10px] px-[10px] pb-[10px] pt-0 md:grid-cols-4">
        {severityOrder.map((severity) => {
          const key = severity.toLowerCase();
          const count = Number(counts[key] ?? 0);
          const delta = Number(trends[key] ?? 0);
          const isRiskSeverity = severity === "CRITICAL" || severity === "HIGH";
          const deltaClass = delta === 0 ? "text-muted-foreground" : (isRiskSeverity ? delta < 0 : delta > 0) ? "text-emerald-600" : "text-destructive";
          return (
            <div
              key={severity}
              className="overflow-hidden rounded-[10px] p-5 sm:p-6"
              style={{ backgroundColor: severityStyle[severity].background }}
            >
              <div className="flex items-center gap-3">
                <p className={`text-[52px] font-medium leading-none tabular-nums ${severityStyle[severity].text}`}>{count}</p>
                <p className="text-sm font-medium">{sentenceCase(severity)}</p>
                <p className={`ml-auto text-xs tabular-nums ${deltaClass}`}>{delta > 0 ? "+" : ""}{delta}%</p>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function NeedsActionNow({ issues, agents, organizationName }: { issues: SecurityIssue[]; agents: Agent[]; organizationName: string }) {
  const agentName = (id: number | null | undefined) => agents.find((agent) => agent.id === id)?.name ?? "Agent unavailable";
  // TODO(data): SLA deadlines are missing. Timestamp order is used only as a stable fallback, not as an SLA proxy.
  const actionable = issues.filter((issue) => issue.status === "OPEN" || issue.status === "IN_REVIEW").sort((a, b) => a.timestamp.localeCompare(b.timestamp));

  return (
    <Card className="rounded-[15px] shadow-none lg:col-span-2">
      <CardHeader className="p-4 pb-3 sm:p-6 sm:pb-3"><CardTitle className="text-base font-medium">Needs action now</CardTitle></CardHeader>
      <CardContent className="p-0">
        {actionable.length === 0 ? <p className="border-t px-6 py-10 text-sm text-muted-foreground">You&apos;re clear. No issues breaching SLA in the last 30 days.</p> : actionable.map((issue) => {
          // TODO(data): metadata.endpoint is optional and may not represent the resource reached.
          const resource = issue.metadata.endpoint ?? "Resource unavailable";
          return (
            <div key={issue.id} className="flex items-start gap-3 border-t px-4 py-4 sm:px-6">
              <Badge variant="outline" className={`mt-0.5 rounded-[5px] font-normal ${severityStyle[issue.severity]?.badge ?? ""}`}>{sentenceCase(issue.severity)}</Badge>
              <Link to={`/issues/${issue.id}`} className="min-w-0 flex-1 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <p className="truncate text-sm font-medium">{issue.title}</p>
                <p className="mt-1 truncate text-xs text-muted-foreground">{organizationName} · {agentName(issue.agentId)} · {resource} · blast radius unavailable</p>
              </Link>
              <Badge variant="outline" className="shrink-0 gap-1 rounded-[5px] font-normal text-muted-foreground"><Clock3 className="size-3" />{issue.status === "IN_REVIEW" ? "In review" : "SLA unavailable"}</Badge>
              <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="size-8 shrink-0"><MoreHorizontal className="size-4" /><span className="sr-only">Issue actions</span></Button></DropdownMenuTrigger><DropdownMenuContent align="end"><DropdownMenuItem onClick={() => toast("Assigned")}>Assign</DropdownMenuItem><DropdownMenuItem onClick={() => toast("Sent to Jira")}>Send to Jira</DropdownMenuItem><DropdownMenuItem onClick={() => toast("Marked false positive")}>Mark false positive</DropdownMenuItem></DropdownMenuContent></DropdownMenu>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

const FRAMEWORK_ISSUE_MAP: Record<string, string[]> = {
  "EU AI Act": ["prompt_injection", "jailbreak_attempt", "pii_exposure", "unauthorized_data_access", "ungrounded_response"],
  "ISO 42001": ["prompt_injection", "secrets_in_output", "unauthorized_data_access", "insecure_tool_call", "verbose_error_leak"],
  "NIST AI RMF": ["prompt_injection", "jailbreak_attempt", "unauthorized_data_access", "excessive_token_usage", "ungrounded_response", "insecure_tool_call"],
  "SOC 2 AI": ["secrets_in_output", "unauthorized_data_access", "pii_exposure", "verbose_error_leak", "insecure_tool_call"],
  "IEEE 7000": ["ungrounded_response", "pii_exposure", "jailbreak_attempt"],
  "GDPR AI": ["pii_exposure", "unauthorized_data_access", "verbose_error_leak"],
};

function ComplianceGaps({ issues }: { issues: SecurityIssue[] }) {
  const scores = useMemo(() => {
    if (issues.length === 0) return null;
    const openTypes = new Set(issues.filter((i) => i.status === "OPEN" || i.status === "IN_REVIEW").map((i) => i.type));
    const allTypes = new Set(issues.map((i) => i.type));
    return Object.entries(FRAMEWORK_ISSUE_MAP).map(([name, controls]) => {
      const relevant = controls.filter((c) => allTypes.has(c));
      if (relevant.length === 0) return { name, score: 100 };
      const passed = relevant.filter((c) => !openTypes.has(c)).length;
      return { name, score: Math.round((passed / relevant.length) * 100) };
    });
  }, [issues]);

  const overall = scores ? Math.round(scores.reduce((s, f) => s + f.score, 0) / scores.length) : null;

  return (
    <Card className="rounded-[15px] shadow-none">
      <CardHeader className="p-4 pb-3 sm:p-6 sm:pb-3"><CardTitle className="text-base font-medium">Compliance gaps</CardTitle></CardHeader>
      <CardContent className="space-y-5 p-4 pt-1 sm:p-6 sm:pt-1">
        {(scores ?? Object.keys(FRAMEWORK_ISSUE_MAP).map((name) => ({ name, score: 0 }))).map((f) => (
          <div key={f.name} className="grid grid-cols-[6.5rem_1fr_auto] items-center gap-3 text-xs">
            <span className="text-muted-foreground">{f.name}</span>
            <Progress value={scores ? f.score : 0} aria-label={`${f.name} ${scores ? f.score + "%" : "unavailable"}`} />
            <span className="w-8 text-right font-medium tabular-nums">{scores ? `${f.score}%` : "—"}</span>
          </div>
        ))}
      </CardContent>
      <CardFooter className="border-t px-4 py-3 text-sm sm:px-6"><span className="text-muted-foreground">Overall</span><span className="ml-auto font-medium tabular-nums">{overall !== null ? `${overall}%` : "—"}</span></CardFooter>
    </Card>
  );
}

function AgentFleet({ agents, organizationName }: { agents: Agent[]; organizationName: string }) {
  const navigate = useNavigate();
  const [status] = useState("all");
  const { isMssp, isAll, orgNameMap } = useTenant();
  const sortedAgents = useMemo(() => [...agents].sort((a, b) => b.criticalIssues - a.criticalIssues || b.highIssues - a.highIssues), [agents]);
  const openAgent = (agent: Agent) => navigate(`/agents/${agent.id}`);
  const tenantName = (agent: Agent) => orgNameMap.get(agent.organizationId) ?? organizationName;
  const showTenantCol = isMssp && isAll;
  return (
    <Card className="rounded-[15px] shadow-none">
      <CardHeader className="flex-row items-center justify-between p-4 sm:p-6"><div><CardTitle className="text-base font-medium">Agent fleet</CardTitle><p className="mt-1 text-xs text-muted-foreground">Ordered by critical and high issue count</p></div><Select value={status} disabled><SelectTrigger className="h-9 w-[160px]"><SelectValue placeholder="Status unavailable" /></SelectTrigger><SelectContent><SelectItem value="all">Status unavailable</SelectItem></SelectContent></Select></CardHeader>
      <CardContent className="p-0">
        <div className="hidden md:block"><Table><TableHeader><TableRow><TableHead>Agent</TableHead>{showTenantCol && <TableHead>Tenant</TableHead>}<TableHead>Type</TableHead><TableHead className="text-right">Critical</TableHead><TableHead className="text-right">High</TableHead><TableHead>Risk score</TableHead><TableHead>Last seen</TableHead><TableHead>Status</TableHead></TableRow></TableHeader><TableBody>{sortedAgents.map((agent) => <TableRow key={agent.id} tabIndex={0} onClick={() => openAgent(agent)} onKeyDown={(event) => { if (event.key === "Enter") openAgent(agent); }} className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"><TableCell><div className="flex items-center gap-3"><AgentProviderLogo provider={agent.modelProvider} className="h-6 w-6 shrink-0" /><div className="min-w-0"><span className="block truncate font-medium">{agent.name}</span><span className="text-xs text-muted-foreground">{agent.modelName}</span></div></div></TableCell>{showTenantCol && <TableCell>{tenantName(agent)}</TableCell>}<TableCell>{sentenceCase(agent.traceSource)}</TableCell><TableCell className="text-right tabular-nums">{agent.criticalIssues}</TableCell><TableCell className="text-right tabular-nums">{agent.highIssues}</TableCell><TableCell className="tabular-nums text-muted-foreground">—</TableCell><TableCell className="text-muted-foreground">{new Date(agent.lastSeenAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</TableCell><TableCell><Badge variant="outline" className="rounded-[5px] font-normal text-muted-foreground">Unavailable</Badge></TableCell></TableRow>)}</TableBody></Table></div>
        <div className="divide-y md:hidden">{sortedAgents.map((agent) => <button key={agent.id} onClick={() => openAgent(agent)} className="flex w-full items-center gap-3 p-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"><AgentProviderLogo provider={agent.modelProvider} className="h-6 w-6 shrink-0" /><div className="min-w-0 flex-1"><p className="truncate text-sm font-medium">{agent.name}</p><p className="truncate text-xs text-muted-foreground">{showTenantCol ? tenantName(agent) : organizationName} · {agent.modelName}</p></div><span className="text-xs text-muted-foreground">Risk —</span><ChevronRight className="size-4 text-muted-foreground" /></button>)}</div>
      </CardContent>
    </Card>
  );
}

export const SecurityDashboard = () => {
  const [searchParams] = useSearchParams();
  const range = (searchParams.get("range") ?? "30d") as keyof typeof rangeDays;
  const days = rangeDays[range] ?? 30;
  const { organization } = useOrganizationContext();
  const { selectedOrgId } = useTenant();
  const { data: issueResponse, isLoading: issuesLoading } = useSecurityIssues({ items_per_page: 1000, organization_id: selectedOrgId });
  const { data: stats, isLoading: statsLoading } = useSecurityIssueStats(days, selectedOrgId);
  const { data: agents = [], isLoading: agentsLoading } = useAIAgents(days, selectedOrgId);
  const issues: SecurityIssue[] = issueResponse?.data ?? [];
  const organizationName = organization?.name ?? "Current organization";

  return (
    <div className="flex flex-1 flex-col bg-muted/10">
      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
        <div className="px-4 lg:px-6">{issuesLoading || agentsLoading ? <PanelSkeleton className="h-[380px]" /> : <ExposureFunnel agents={agents} issues={issues} organizationName={organizationName} range={range} />}</div>
        <div className="px-4 lg:px-6">{statsLoading ? <PanelSkeleton className="h-[260px]" /> : <OpenIssuesBySeverity stats={stats} range={range} />}</div>
        <div className="grid gap-4 px-4 lg:grid-cols-3 lg:px-6">{issuesLoading || agentsLoading ? <PanelSkeleton className="h-[330px] lg:col-span-2" /> : <NeedsActionNow issues={issues} agents={agents} organizationName={organizationName} />}<ComplianceGaps issues={issues} /></div>
        <div className="px-4 lg:px-6">{agentsLoading ? <PanelSkeleton className="h-[320px]" /> : <AgentFleet agents={agents} organizationName={organizationName} />}</div>
      </div>
    </div>
  );
};
